import { getAuthToken, logout as authLogout } from '../services/authService';

// Re-export getAuthToken for backward compatibility
export { getAuthToken };

// Helper function to handle 401 errors
const handleUnauthorized = () => {
  authLogout();
  // Small delay before redirect to allow logout event handlers to process
  setTimeout(() => {
    window.location.href = '/';
  }, 100);
};

// Improved timezone conversion utilities with proper timezone handling
export const convertLocalTimeToEpoch = (localTime, date, timezone = Intl.DateTimeFormat().resolvedOptions().timeZone) => {
  if (!localTime || !date) {
    throw new Error('Both localTime and date are required');
  }
  
  const [hours, minutes] = localTime.split(':');
  if (isNaN(hours) || isNaN(minutes)) {
    throw new Error('Invalid time format. Expected HH:MM');
  }
  
  // Create a date object in the user's local timezone
  const localDateTime = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    parseInt(hours),
    parseInt(minutes),
    0,
    0
  );
  
  // Convert to UTC epoch
  const utcEpoch = Math.floor(localDateTime.getTime() / 1000);
  
  return utcEpoch;
};

export const convertEpochToLocalTime = (epoch, timezone = Intl.DateTimeFormat().resolvedOptions().timeZone) => {
  if (!epoch || isNaN(epoch)) {
    throw new Error('Invalid epoch timestamp');
  }
  
  const date = new Date(epoch * 1000); // Convert epoch seconds to milliseconds
  
  // Validate the date is valid
  if (isNaN(date.getTime())) {
    throw new Error('Invalid epoch timestamp');
  }
  
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: timezone
  });
};

export const convertEpochToLocalDate = (epoch, timezone = Intl.DateTimeFormat().resolvedOptions().timeZone) => {
  if (!epoch || isNaN(epoch)) {
    throw new Error('Invalid epoch timestamp');
  }
  
  const date = new Date(epoch * 1000);
  
  // Validate the date is valid
  if (isNaN(date.getTime())) {
    throw new Error('Invalid epoch timestamp');
  }
  
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: timezone
  });
};

export const formatTimeRange = (startEpoch, endEpoch, timezone = Intl.DateTimeFormat().resolvedOptions().timeZone) => {
  if (!startEpoch || !endEpoch || startEpoch >= endEpoch) {
    throw new Error('Invalid time range');
  }
  
  const startTime = convertEpochToLocalTime(startEpoch, timezone);
  const endTime = convertEpochToLocalTime(endEpoch, timezone);
  return `${startTime} - ${endTime}`;
};

export const calculateDuration = (startEpoch, endEpoch) => {
  if (!startEpoch || !endEpoch || startEpoch >= endEpoch) {
    throw new Error('Invalid time range');
  }
  
  const durationMinutes = Math.floor((endEpoch - startEpoch) / 60);
  const hours = Math.floor(durationMinutes / 60);
  const minutes = durationMinutes % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes} minutes`;
};

// Helper function to get user's timezone for debugging
export const getUserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// Helper function to get week range (3 days before + today + 3 days after)
export const getWeekRange = (date) => {
  const threeDaysBefore = new Date(date);
  threeDaysBefore.setDate(date.getDate() - 3);
  
  const threeDaysAfter = new Date(date);
  threeDaysAfter.setDate(date.getDate() + 3);
  
  return {
    start: threeDaysBefore,
    end: threeDaysAfter
  };
};

// Helper function to check if date is in the past
export const isPastDate = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
};

// Helper function to check if time is in the past for today
export const isPastTime = (date, time) => {
  const now = new Date();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // If not today, it's not past
  if (date < today || date > today) return false;
  
  const [hours, minutes] = time.split(':');
  const selectedDateTime = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    parseInt(hours),
    parseInt(minutes)
  );
  
  return selectedDateTime < now;

};

// Helper function to generate week key for caching
export const getWeekKey = (date) => {
  const weekRange = getWeekRange(date);
  return weekRange.start.toISOString().split('T')[0];
};

// Helper function to check if demo mode is enabled (from sessionStorage)
const isDemoModeEnabled = () => {
  try {
    return sessionStorage.getItem('theraengage_demo_mode') === 'true';
  } catch (error) {
    // sessionStorage might not be available in some contexts
    return false;
  }
};

// Authenticated fetch wrapper with improved error handling
export const authenticatedFetch = async (url, options = {}, userId = null, tokenOverride = null) => {
  // Use provided token override, or get token from auth service
  let token = tokenOverride;
  
  if (!token) {
    const tokenResult = getAuthToken();
    // Handle both Promise (Auth0) and string (legacy) cases
    if (tokenResult instanceof Promise) {
      token = await tokenResult;
    } else {
      token = tokenResult;
    }
  }
  
  if (!token) {
    throw new Error('No authentication token available or token expired');
  }

  // Automatically check demo mode from sessionStorage
  const demoMode = isDemoModeEnabled();

  try {
    // Calculate body size for logging
    const bodySize = options.body ? options.body.length : 0;
    const bodySizeMB = bodySize ? (bodySize / (1024 * 1024)).toFixed(2) : '0';
    
    // Log API calls for debugging
    console.log('🌐 API Call:', {
      method: options.method || 'GET',
      url: url,
      hasBody: !!options.body,
      bodySize: bodySize,
      bodySizeMB: bodySizeMB,
      userId: userId,
      demoMode: demoMode
    });
    
    // Warn if body is large (potential size limit issue)
    if (bodySize > 5 * 1024 * 1024) { // 5MB
      console.warn('⚠️ Large request body detected:', {
        url,
        bodySizeMB,
        warning: 'Request body exceeds 5MB - may hit Netlify 6MB limit'
      });
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0',
      ...options.headers,
    };

    // Add userId header if provided
    if (userId) {
      headers['X-User-ID'] = userId;
    }

    // Automatically add demo mode header if enabled (from sessionStorage)
    if (demoMode) {
      headers['X-Demo-Mode'] = 'true';
    }

    // Add cache-busting parameter to prevent HTTP caching
    const cacheBuster = Date.now();
    const separator = url.includes('?') ? '&' : '?';
    const urlWithCacheBuster = `${url}${separator}_t=${cacheBuster}`;

    console.log('🌐 Sending fetch request...', {
      url: urlWithCacheBuster,
      method: options.method || 'GET',
      bodySizeMB: bodySizeMB
    });
    
    let response;
    try {
      response = await fetch(urlWithCacheBuster, {
        ...options,
        headers,
      });
      console.log('🌐 Fetch request completed, got response');
    } catch (fetchError) {
      // Track fetch errors to PostHog if analytics service is available
      // Only track for photo upload endpoint to avoid spam
      if (url.includes('upload-task-photo') && typeof window !== 'undefined' && window.analyticsService) {
        try {
          window.analyticsService.track('photo_api_fetch_error', {
            errorName: fetchError.name,
            errorMessage: fetchError.message?.substring(0, 500),
            errorStack: fetchError.stack?.substring(0, 1000),
            url: url,
            bodySizeMB: bodySizeMB,
            timestamp: new Date().toISOString()
          });
        } catch (trackError) {
          // Don't break if tracking fails
          console.error('Failed to track fetch error:', trackError);
        }
      }
      
      throw fetchError;
    }

    // Log response for debugging
    console.log('🌐 API Response received:', {
      method: options.method || 'GET',
      url: url,
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      bodySizeMB: bodySizeMB
    });

    if (response.status === 401) {
      handleUnauthorized();
      throw new Error('Unauthorized - Please log in again');
    }

    if (response.status === 403) {
      throw new Error('Access denied - Insufficient permissions');
    }

    if (response.status >= 500) {
      // Read response body before throwing to preserve error details
      let errorBody = '';
      try {
        errorBody = await response.text();
      } catch (e) {
        errorBody = '';
      }
      
      // Track server errors to PostHog if analytics service is available
      // Only track for photo upload endpoint to avoid spam
      if (url.includes('upload-task-photo') && typeof window !== 'undefined' && window.analyticsService) {
        try {
          window.analyticsService.track('photo_api_server_error', {
            status: response.status,
            statusText: response.statusText,
            url: url,
            bodySizeMB: bodySizeMB,
            responseBody: errorBody.substring(0, 1000),
            timestamp: new Date().toISOString()
          });
        } catch (trackError) {
          // Don't break if tracking fails
        }
      }
      
      // Create error with response body included
      const error = new Error(`Server error - Please try again later`);
      error.status = response.status;
      error.responseBody = errorBody;
      error.originalUrl = url;
      error.requestBodySizeMB = bodySizeMB;
      throw error;
    }

    return response;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Network error - Please check your connection');
    }
    throw error;
  }
};

// Retry configuration
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 5000
};

// Retry helper function
const retryWithBackoff = async (fn, retries = RETRY_CONFIG.maxRetries) => {
  try {
    return await fn();
  } catch (error) {
    // Don't retry on 401 (Unauthorized) or 403 (Forbidden) errors
    // These are authentication/authorization failures that won't succeed on retry
    if (error.message.includes('Unauthorized') || error.message.includes('Forbidden')) {
      throw error;
    }
    
    // Only retry on network or server errors
    if (retries > 0 && (error.message.includes('Network error') || error.message.includes('Server error'))) {
      const delay = Math.min(RETRY_CONFIG.baseDelay * (RETRY_CONFIG.maxRetries - retries + 1), RETRY_CONFIG.maxDelay);
      await new Promise(resolve => setTimeout(resolve, delay));
      return retryWithBackoff(fn, retries - 1);
    }
    throw error;
  }
};

// API helper functions with retry logic
export const apiGet = async (url, userId = null, tokenOverride = null) => {
  return retryWithBackoff(async () => {
    const response = await authenticatedFetch(url, {}, userId, tokenOverride);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }
    return response.json();
  });
};

export const apiPost = async (url, data, userId = null) => {
  return retryWithBackoff(async () => {
    try {
      const response = await authenticatedFetch(url, {
        method: 'POST',
        body: JSON.stringify(data),
      }, userId);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      return response.json();
    } catch (error) {
      // If error has responseBody from authenticatedFetch, include it in the error message
      if (error.responseBody) {
        throw new Error(`HTTP error! status: ${error.status || 500}, message: ${error.responseBody}`);
      }
      throw error;
    }
  });
};

export const apiPut = async (url, data, userId = null) => {
  return retryWithBackoff(async () => {
    const response = await authenticatedFetch(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    }, userId);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }
    return response.json();
  });
};

export const apiDelete = async (url, data = {}, userId = null) => {
  return retryWithBackoff(async () => {
    const response = await authenticatedFetch(url, {
      method: 'DELETE',
      body: JSON.stringify(data),
    }, userId);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }
    return response.json();
  });
}; 